import * as THREE from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import Application from '../Application';
import EventEmitter from '../Utils/EventEmitter';
// The page shown on the monitor: the screen site in apps/screen, published at /screen/
const SCREEN_SITE = '/screen/';
const SCREEN_SIZE = { w: 1984, h: 1024 };
// The iframe is scaled up so it fills the terminal's whole screen bay
const SCREEN_SCALE = 1.242;
const IFRAME_PADDING = 32;
const IFRAME_SIZE = {
    w: SCREEN_SIZE.w - IFRAME_PADDING,
    h: SCREEN_SIZE.h - IFRAME_PADDING,
};
// The page's hole and the layers over it are a few units apart, closer than the depth buffer can tell apart from
// across the room (phones especially), so they flickered through each other in patches. They don't write depth and
// are drawn in a fixed order instead, and are nudged a few depth steps towards the camera so the bezel around them
// never wins either. Everything in front (the chair) still hides them: they still test against what's drawn.
function onTop(material) {
    material.depthWrite = false;
    material.polygonOffset = true;
    material.polygonOffsetFactor = -1;
    material.polygonOffsetUnits = -4;
}
export default class MonitorScreen extends EventEmitter {
    constructor() {
        super();
        this.application = new Application();
        this.scene = this.application.scene;
        this.cssScene = this.application.cssScene;
        this.sizes = this.application.sizes;
        this.resources = this.application.resources;
        this.screenSize = new THREE.Vector2(SCREEN_SIZE.w * SCREEN_SCALE, SCREEN_SIZE.h * SCREEN_SCALE);
        this.camera = this.application.camera;
        this.position = new THREE.Vector3(-357, 914, -117);
        this.rotation = new THREE.Euler(0, 0, 0);
        this.videoTextures = {};
        this.mouseClickInProgress = false;
        this.shouldLeaveMonitor = false;
        // Create screen
        this.initializeScreenEvents();
        this.createIframe();
        const maxOffset = this.createTextureLayers();
        this.createEnclosingPlanes(maxOffset);
        this.createPerspectiveDimmer(maxOffset);
    }
    initializeScreenEvents() {
        document.addEventListener('mousemove', (event) => {
            var _a;
            const id = event.target.id;
            if (id === 'computer-screen') {
                event.inComputer = true;
            }
            this.inComputer = (_a = event.inComputer) !== null && _a !== void 0 ? _a : false;
            if (this.inComputer && !this.prevInComputer) {
                this.camera.trigger('enterMonitor');
            }
            if (!this.inComputer &&
                this.prevInComputer &&
                !this.mouseClickInProgress) {
                this.camera.trigger('leftMonitor');
            }
            if (!this.inComputer &&
                this.mouseClickInProgress &&
                this.prevInComputer) {
                this.shouldLeaveMonitor = true;
            }
            else {
                this.shouldLeaveMonitor = false;
            }
            this.application.mouse.trigger('mousemove', [event]);
            this.prevInComputer = this.inComputer;
        }, false);
        document.addEventListener('mousedown', (event) => {
            var _a;
            this.inComputer = (_a = event.inComputer) !== null && _a !== void 0 ? _a : false;
            this.application.mouse.trigger('mousedown', [event]);
            this.mouseClickInProgress = true;
            this.prevInComputer = this.inComputer;
        }, false);
        document.addEventListener('mouseup', (event) => {
            var _a;
            this.inComputer = (_a = event.inComputer) !== null && _a !== void 0 ? _a : false;
            this.application.mouse.trigger('mouseup', [event]);
            if (this.shouldLeaveMonitor) {
                this.camera.trigger('leftMonitor');
                this.shouldLeaveMonitor = false;
            }
            this.mouseClickInProgress = false;
            this.prevInComputer = this.inComputer;
        }, false);
    }
    /**
     * Creates the iframe for the computer screen
     */
    createIframe() {
        // Create container
        const container = document.createElement('div');
        container.style.width = SCREEN_SIZE.w + 'px';
        container.style.height = SCREEN_SIZE.h + 'px';
        container.style.opacity = '1';
        container.style.background = '#1d2e2f';
        container.style.position = 'relative';
        // Fallback overlay shown while loading; switches to error state on timeout
        const { element: fallback, hide: hideFallback, timeout: fallbackTimeout } = this.createFallbackOverlay();
        container.appendChild(fallback);
        // Create iframe
        const iframe = document.createElement('iframe');
        // Bubble mouse move events to the main application, so we can affect the camera
        iframe.onload = () => {
            clearTimeout(fallbackTimeout);
            hideFallback();
            if (iframe.contentWindow) {
                window.addEventListener('message', (event) => {
                    const extra = { inComputer: true };
                    if (event.data.type === 'mousemove') {
                        const clRect = iframe.getBoundingClientRect();
                        const { top, left, width, height } = clRect;
                        extra.clientX = Math.round(event.data.clientX * (width / IFRAME_SIZE.w) + left);
                        extra.clientY = Math.round(event.data.clientY * (height / IFRAME_SIZE.h) + top);
                    }
                    else if (event.data.type === 'keydown' ||
                        event.data.type === 'keyup') {
                        extra.key = event.data.key;
                    }
                    const evt = Object.assign(new CustomEvent(event.data.type, {
                        bubbles: true,
                        cancelable: false,
                    }), extra);
                    iframe.dispatchEvent(evt);
                });
            }
        };
        iframe.src = SCREEN_SITE;
        iframe.style.width = SCREEN_SIZE.w + 'px';
        iframe.style.height = SCREEN_SIZE.h + 'px';
        iframe.style.padding = IFRAME_PADDING + 'px';
        iframe.style.boxSizing = 'border-box';
        iframe.style.opacity = '1';
        iframe.className = 'jitter';
        iframe.id = 'computer-screen';
        iframe.frameBorder = '0';
        iframe.title = 'MonogumOS';
        // Add iframe to container
        container.appendChild(iframe);
        // Create CSS plane
        this.createCssPlane(container);
    }
    /**
     * Builds the loading/error overlay shown inside the monitor container while
     * the iframe is connecting. Returns:
     *   element — the overlay div to append to the container
     *   hide    — call on successful iframe load to remove the overlay
     *   timeout — the pending fallback timer (pass to clearTimeout on success)
     */
    createFallbackOverlay() {
        const overlay = document.createElement('div');
        Object.assign(overlay.style, {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#00ff00',
            fontFamily: 'monospace',
            fontSize: '16px',
            letterSpacing: '1px',
            pointerEvents: 'none',
            zIndex: '1',
        });
        const loadingText = document.createElement('p');
        loadingText.textContent = 'CONNECTING...';
        overlay.appendChild(loadingText);
        const errorText = document.createElement('p');
        Object.assign(errorText.style, {
            display: 'none',
            marginTop: '16px',
            color: '#aaaaaa',
        });
        errorText.textContent = 'Unable to connect.';
        overlay.appendChild(errorText);
        const fallbackLink = document.createElement('a');
        fallbackLink.href = SCREEN_SITE;
        fallbackLink.target = '_blank';
        fallbackLink.rel = 'noopener noreferrer';
        fallbackLink.textContent = '→ Open the screen in a new tab';
        Object.assign(fallbackLink.style, {
            display: 'none',
            marginTop: '12px',
            color: '#4598ff',
            textDecoration: 'underline',
            cursor: 'pointer',
            pointerEvents: 'auto',
        });
        overlay.appendChild(fallbackLink);
        // After 10 s with no load event, surface the direct link
        const timeout = setTimeout(() => {
            loadingText.style.display = 'none';
            errorText.style.display = 'block';
            fallbackLink.style.display = 'block';
        }, 10000);
        return {
            element: overlay,
            hide: () => { overlay.style.display = 'none'; },
            timeout,
        };
    }
    /**
     * Creates a CSS plane and GL plane to properly occlude the CSS plane
     * @param element the element to create the css plane for
     */
    createCssPlane(element) {
        // Create CSS3D object
        const object = new CSS3DObject(element);
        // copy monitor position and rotation
        object.position.copy(this.position);
        object.rotation.copy(this.rotation);
        object.scale.setScalar(SCREEN_SCALE);
        // Add to CSS scene
        this.cssScene.add(object);
        // Create GL plane
        const material = new THREE.MeshLambertMaterial();
        material.side = THREE.DoubleSide;
        material.opacity = 0;
        material.transparent = true;
        // NoBlending allows the GL plane to occlude the CSS plane
        material.blending = THREE.NoBlending;
        onTop(material);
        // Create plane geometry
        const geometry = new THREE.PlaneGeometry(SCREEN_SIZE.w, SCREEN_SIZE.h);
        // Create the GL plane mesh
        const mesh = new THREE.Mesh(geometry, material);
        // Copy the position, rotation and scale of the CSS plane to the GL plane
        mesh.position.copy(object.position);
        mesh.rotation.copy(object.rotation);
        mesh.scale.copy(object.scale);
        // drawn first of the screen's stack: the hole, then the layers over it (see onTop)
        mesh.renderOrder = 1;
        // Add to gl scene
        this.scene.add(mesh);
    }
    /**
     * Creates the texture layers for the computer screen
     * @returns the maximum offset of the texture layers
     */
    createTextureLayers() {
        const textures = this.resources.items.texture;
        this.getVideoTextures('screen-crt');
        // Scale factor to multiply depth offset by
        const scaleFactor = 4;
        // The layers over the page (made by assets/screen/make_screen_layers.py): the CRT glow, rolling refresh and
        // grain are added on top; the glass (scanlines, darker curved edges) is laid over everything. Both sit almost
        // flush with the page: further out they slid against it when seen at an angle (the page peeked out under the
        // glass's dark edges) and came close to the bezel's faces, where they flickered
        const layers = {
            crt: {
                texture: this.videoTextures['screen-crt'],
                blending: THREE.AdditiveBlending,
                opacity: 0.22,
                offset: 1,
            },
            glass: {
                texture: textures.screenGlassTexture,
                blending: THREE.NormalBlending,
                opacity: 1,
                offset: 2,
            },
        };
        // Declare max offset
        let maxOffset = -1;
        let order = 2;
        // Add the texture layers to the screen
        for (const [_, layer] of Object.entries(layers)) {
            const offset = layer.offset * scaleFactor;
            this.addTextureLayer(layer.texture, layer.blending, layer.opacity, offset, order++);
            // Calculate the max offset
            if (offset > maxOffset)
                maxOffset = offset;
        }
        // Return the max offset
        return maxOffset;
    }
    getVideoTextures(videoId) {
        const video = document.getElementById(videoId);
        if (!video) {
            setTimeout(() => {
                this.getVideoTextures(videoId);
            }, 100);
        }
        else {
            this.videoTextures[videoId] = new THREE.VideoTexture(video);
        }
    }
    /**
     * Adds a texture layer to the screen
     * @param texture the texture to add
     * @param blending the blending mode
     * @param opacity the opacity of the texture
     * @param offset the offset of the texture, higher values are further from the screen
     * @param order the drawing order within the screen's stack
     */
    addTextureLayer(texture, blendingMode, opacity, offset, order) {
        // Create material
        const material = new THREE.MeshBasicMaterial({
            map: texture,
            blending: blendingMode,
            side: THREE.DoubleSide,
            opacity,
            transparent: true,
        });
        onTop(material);
        // Create geometry
        const geometry = new THREE.PlaneGeometry(this.screenSize.width, this.screenSize.height);
        // Create mesh
        const mesh = new THREE.Mesh(geometry, material);
        mesh.renderOrder = order;
        // Copy position and apply the depth offset
        mesh.position.copy(this.offsetPosition(this.position, new THREE.Vector3(0, 0, offset)));
        // Copy rotation
        mesh.rotation.copy(this.rotation);
        this.scene.add(mesh);
    }
    /**
     * Creates enclosing planes for the computer screen
     * @param maxOffset the maximum offset of the texture layers
     */
    createEnclosingPlanes(maxOffset) {
        // Create planes, lots of boiler plate code here because I'm lazy
        const planes = {
            left: {
                size: new THREE.Vector2(maxOffset, this.screenSize.height),
                position: this.offsetPosition(this.position, new THREE.Vector3(-this.screenSize.width / 2, 0, maxOffset / 2)),
                rotation: new THREE.Euler(0, 90 * THREE.MathUtils.DEG2RAD, 0),
            },
            right: {
                size: new THREE.Vector2(maxOffset, this.screenSize.height),
                position: this.offsetPosition(this.position, new THREE.Vector3(this.screenSize.width / 2, 0, maxOffset / 2)),
                rotation: new THREE.Euler(0, 90 * THREE.MathUtils.DEG2RAD, 0),
            },
            top: {
                size: new THREE.Vector2(this.screenSize.width, maxOffset),
                position: this.offsetPosition(this.position, new THREE.Vector3(0, this.screenSize.height / 2, maxOffset / 2)),
                rotation: new THREE.Euler(90 * THREE.MathUtils.DEG2RAD, 0, 0),
            },
            bottom: {
                size: new THREE.Vector2(this.screenSize.width, maxOffset),
                position: this.offsetPosition(this.position, new THREE.Vector3(0, -this.screenSize.height / 2, maxOffset / 2)),
                rotation: new THREE.Euler(90 * THREE.MathUtils.DEG2RAD, 0, 0),
            },
        };
        // Add each of the planes
        for (const [_, plane] of Object.entries(planes)) {
            this.createEnclosingPlane(plane);
        }
    }
    /**
     * Creates a plane for the enclosing planes
     * @param plane the plane to create
     */
    createEnclosingPlane(plane) {
        const material = new THREE.MeshBasicMaterial({
            side: THREE.DoubleSide,
            color: 0x101010,
        });
        const geometry = new THREE.PlaneGeometry(plane.size.x, plane.size.y);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.copy(plane.position);
        mesh.rotation.copy(plane.rotation);
        this.scene.add(mesh);
    }
    createPerspectiveDimmer(maxOffset) {
        const material = new THREE.MeshBasicMaterial({
            side: THREE.DoubleSide,
            color: 0x000000,
            transparent: true,
            blending: THREE.AdditiveBlending,
        });
        const plane = new THREE.PlaneGeometry(this.screenSize.width, this.screenSize.height);
        onTop(material);
        const mesh = new THREE.Mesh(plane, material);
        mesh.renderOrder = 10;
        mesh.position.copy(this.offsetPosition(this.position, new THREE.Vector3(0, 0, maxOffset - 5)));
        mesh.rotation.copy(this.rotation);
        this.dimmingPlane = mesh;
        this.scene.add(mesh);
    }
    /**
     * Offsets a position vector by another vector
     * @param position the position to offset
     * @param offset the offset to apply
     * @returns the new offset position
     */
    offsetPosition(position, offset) {
        const newPosition = new THREE.Vector3();
        newPosition.copy(position);
        newPosition.add(offset);
        return newPosition;
    }
    update() {
        if (this.dimmingPlane) {
            const planeNormal = new THREE.Vector3(0, 0, 1);
            const viewVector = new THREE.Vector3();
            viewVector.copy(this.camera.instance.position);
            viewVector.sub(this.position);
            viewVector.normalize();
            const dot = viewVector.dot(planeNormal);
            // calculate the distance from the camera vector to the plane vector
            const dimPos = this.dimmingPlane.position;
            const camPos = this.camera.instance.position;
            const distance = Math.sqrt(Math.pow((camPos.x - dimPos.x), 2) +
                Math.pow((camPos.y - dimPos.y), 2) +
                Math.pow((camPos.z - dimPos.z), 2));
            const opacity = 1 / (distance / 10000);
            const DIM_FACTOR = 0.7;
            // @ts-ignore
            this.dimmingPlane.material.opacity =
                (1 - opacity) * DIM_FACTOR + (1 - dot) * DIM_FACTOR;
        }
    }
}
